import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { ApiResource } from "@auvious/common";

import { AuviousRtcService } from "./rtc.service";
import {
  IApplication,
  IApplicationConfig,
  IApplicationDTO,
} from "../models/IApplication";
import { ApplicationTypeEnum } from "../core-ui.enums";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { GenericErrorHandler } from "./error-handlers.service";

/** how long agent personal data of the organization is kept after a login */
export interface IPersonalDataRetention {
  /** null when the default applies */
  days: number | null;
  defaultDays: number;
  maxDays: number;
}

const PERSONAL_DATA_RETENTION_URL = "personal-data-retention";

@Injectable()
export class ApplicationService {
  private rtcService = inject(AuviousRtcService);
  private httpClient = inject(HttpClient);
  private errorHandler = inject(GenericErrorHandler);

  private appResource: ApiResource;
  private organizationResource: ApiResource;
  private activeApplication: IApplication;

  private _applicationChanged = new BehaviorSubject<IApplication>(undefined);
  public applicationChanged$ = this._applicationChanged.asObservable();

  createApiResource() {
    if (!this.rtcService.getAuviousCommonClient()) {
      throw new Error("Not Found: Auvious Common Client");
    }

    if (!!this.appResource) {
      return;
    }

    this.appResource = this.rtcService
      .getAuviousCommonClient()
      .apiResourceFactory("security");
    this.organizationResource = this.rtcService
      .getAuviousCommonClient()
      .apiResourceFactory("api/organizations");
  }

  get isTalkdeskApp() {
    return (
      this.activeApplication?.getType() === ApplicationTypeEnum.TalkdeskOpenID
    );
  }

  get isGenesysCloudApp() {
    return (
      this.activeApplication?.getType() === ApplicationTypeEnum.GenesysCloud
    );
  }

  get isNiceApp() {
    return this.activeApplication?.getType() === ApplicationTypeEnum.NiceOpenID;
  }

  get isVerinApp() {
    return (
      this.activeApplication?.getType() === ApplicationTypeEnum.verintOpenID
    );
  }

  get isStandaloneApp() {
    return (
      this.activeApplication?.getType() === ApplicationTypeEnum.StandaloneOpenID
    );
  }

  getActiveApplication(): IApplication {
    return this.activeApplication;
  }

  setActiveApplication(application: IApplication) {
    this.activeApplication = application;
    this._applicationChanged.next(application);
  }

  get(applicationId: string): Promise<IApplicationDTO> {
    if (!!this.appResource) {
      return this.appResource
        .get({
          urlPostfix: `applications/${applicationId}`,
        })
        .catch((ex) => {
          this.errorHandler.handleNotAuthenticatedError(ex);
          throw ex;
        });
    }

    return firstValueFrom(
      this.httpClient.get<IApplicationDTO>(
        `/security/applications/${applicationId}`
      )
    );
  }

  create(
    type: ApplicationTypeEnum,
    config: IApplicationConfig
  ): Promise<{ id: string }> {
    const app: IApplicationDTO = { type, config };
    return this.appResource
      ?.create(app, { urlPostfix: "applications" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  update(
    applicationId: string,
    type: ApplicationTypeEnum,
    config: IApplicationConfig
  ): Promise<void> {
    const app: IApplicationDTO = { type, config };
    return this.appResource
      ?.update(app, {
        urlPostfix: `applications/${applicationId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  getPersonalDataRetention(): Promise<IPersonalDataRetention> {
    return this.organizationResource
      ?.get({ urlPostfix: PERSONAL_DATA_RETENTION_URL })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /** null restores the default retention */
  updatePersonalDataRetention(days: number | null): Promise<void> {
    const config = { urlPostfix: PERSONAL_DATA_RETENTION_URL };
    const request =
      days == null
        ? this.organizationResource?.delete(config)
        : this.organizationResource?.update({ days }, config);
    return request?.catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  remove(applicationId: string): Promise<void> {
    return this.appResource
      ?.delete({ urlPostfix: `applications/${applicationId}` })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }
}
